/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import type * as React from 'react';
import { Component } from 'react';
import type { DownloadableFile } from '../../../../types/DownloadableFile';
import api from '../../../api';
import Modal from '../Modal';
import type { ModalProps } from '../Modal/presenter';
import Result from './presenter/Result';
import Waiting from './presenter/Waiting';

const modalConfig: {
    [key: string]: {
        status: ModalProps['status'];
        size: ModalProps['size'];
    };
} = {
    DEFAULT: { status: undefined, size: 'MD_WIDTH' },
    PROGRESS: { status: 'waiting', size: 'MD_WIDTH' },
    DONE: { status: 'success', size: 'MD_WIDTH' },
};

interface BodyComponentProps {
    onExport: (file: DownloadableFile) => void;
    onCancel: () => void;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    bodyComponent: React.ComponentType<BodyComponentProps>;
}

interface State {
    status: keyof typeof modalConfig;
    file: DownloadableFile | null;
}

export default class ExportModal extends Component<Props, State> {
    state: State = {
        status: 'DEFAULT',
        file: null,
    };

    static propTypes = {
        isOpen: PropTypes.bool.isRequired,
        onClose: PropTypes.func,
        bodyComponent: PropTypes.func,
    };

    intervalFnId: NodeJS.Timeout | undefined = undefined;

    handleStart = (downloadableFile: DownloadableFile) => {
        this.setState({ status: 'PROGRESS' });
        this.intervalFnId = setInterval(() => {
            api.downloads.getFile(downloadableFile.id).then(d => {
                if (!d.canBeDownloaded) {
                    return;
                }
                this.setState({
                    status: 'DONE',
                    file: d,
                });
                this.cleanup();
            });
        }, 3000);
    };

    cleanup() {
        if (this.intervalFnId) {
            clearInterval(this.intervalFnId);
        }
    }

    componentWillUnmount() {
        this.cleanup();
    }

    render() {
        const { status, file } = this.state;
        const { bodyComponent: BodyComponent, onClose, isOpen } = this.props;
        return (
            <Modal
                noCloseIcon={false}
                isOpen={isOpen}
                onClose={this.props.onClose}
                status={modalConfig[status].status}
                size={modalConfig[status].size}
            >
                {status === 'PROGRESS' ? (
                    <Waiting />
                ) : status === 'DONE' ? (
                    <Result file={file} onClose={onClose} />
                ) : (
                    <BodyComponent onExport={this.handleStart} onCancel={onClose} />
                )}
            </Modal>
        );
    }
}
